import {FC, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Container, Dimmer, Loader, Tab} from 'semantic-ui-react';
import {useApi, api} from '../../api/api';
import {ActionAccess} from '../../api/base';
import {Request, RequestField, RequestSorting} from '../../api/requests';
import {AuthContext, ROLES} from '../../components/Authentication';
import {SyncButton} from '../../components/buttons';
import FileWindow from '../../components/FileWindow';
import MessageWindow from '../../components/MessageWindow';
import Table, {ActionsCell, ButtonCell, TextCell} from '../../components/Table';
import { envColor } from '../../common';


const Requests: FC<{ requestType: string }> = ({requestType}) => {
    const {user} = useContext(AuthContext);
    const isAdmin = user?.roles.find(role => [ROLES.ADMIN, ROLES.SUPER_ADMIN].includes(role));
    const isSuperAdmin = user?.roles.find(role => role === ROLES.SUPER_ADMIN);

    const requestsApi = useApi(api.requests);
    const [requests, setRequests] = useState<Request[]>([]);
    const [loading, setLoading] = useState(false);
    const [payloadView, setPayloadView] = useState<{ name: string, content: string }>();
    const [currentAlert, setAlert] = useState('');
    const sortRef = useRef<RequestSorting>(undefined);
    const [sort, setSortImpl] = useState<RequestSorting>(sortRef.current);

    const load = async () => {
        setLoading(true);
        return requestsApi
            .index(isAdmin ? undefined : user?.id, sortRef.current)
            .then(setRequests)
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    const cancel = async (r: Request) => {
        const {id: targetId} = r;
        if (targetId === undefined) return;

        if (r.submitted_by === user?.id || isAdmin) {
            setLoading(true);
            requestsApi
                .cancel(targetId)
                .then(cancelled => {
                    if (cancelled) {
                        load();
                    }
                });
        }
    };

    const reject = async (r: Request) => {
        const {id: targetId} = r;
        if (targetId === undefined) return;

        if (user && (r.submitted_by === user?.id || isAdmin)) {
            setLoading(true);
            requestsApi
                .reject(targetId, user.id)
                .then(rejected => {
                    if (rejected) {
                        load();
                    } else {
                        setAlert("Failed to reject. Request is invalid or already rejected.");
                        setLoading(false);
                    }
                })
                .catch(err => {
                    console.error("Failed to reject request : ", err);
                    setLoading(false);
                })
        }
    };

    const approve = async (r: Request) => {
        const {id: targetId} = r;

        if (targetId && user) {
            setLoading(true);
            requestsApi
                .approve(targetId, user.id)
                .then(approved => {
                    if (approved) {
                        load();
                    } else {
                        setAlert("Failed to approve. Request is invalid or already approved.");
                        setLoading(false);
                    }
                })
                .catch(err => {
                    console.error("Failed to approve request : ", err);
                    setLoading(false);
                })
        }
    };

    const openPayload = async (data: any) => {
        const [body, url] = data
        let bodyJson = JSON.parse(body)
        let outputString = ''

        if (url === "configs/remove") {
            const content = bodyJson.content
            bodyJson.content = JSON.parse(content)
        } else if (["configs/write", "venueLibs/write"].includes(url)) {
            const data = bodyJson.data
            for (const dataKey in data) {
                const contentBase64 = data[dataKey].content
                const content = Buffer.from(contentBase64, 'base64').toString()

                if (url === "configs/write") {
                    try {
                        data[dataKey].content = JSON.parse(content)
                    } catch (SyntaxError) {
                        data[dataKey].content = 'Wrong file format or content'
                    }
                } else if (url === "venueLibs/write") {
                    outputString += "Item " + (parseInt(dataKey) + 1) + ":\n\n"
                    outputString += "name:\n" + data[dataKey].name + "\n\n"
                    outputString += "author:\n" + data[dataKey].author + "\n\n"
                    outputString += "content:\n" + content + "\n\n"
                }
            }
        }

        setPayloadView({name: '', content: outputString.length ? outputString : JSON.stringify(bodyJson, null, 4)});
    };

    useEffect(() => {
        load()
    }, [sort]);

    const tableActions = useMemo(() => {
        const actions: { icon: string, onClick(r: Request): void }[] = [];
        if (requestsApi.getAccessLevel('reject') === ActionAccess.ALLOWED) {
            actions.push({icon: 'cancel', onClick: reject});
        }
        // if (requestsApi.getAccessLevel('approve') === ActionAccess.ALLOWED) {
        // TODO: Access rights&roles needs to be reviewed more comprehensively later.
        // For now we can just prevent ability to approve own requests.
        if (isSuperAdmin) {
            actions.push({icon: 'check', onClick: approve});
        }

        return actions;
    }, [user]);

    if (!user) {
        return null;
    }

    const setSorting = (sort: RequestSorting) => {
        sortRef.current = sort;
        setSortImpl(sort);
    };

    const sortByColumn = (sortField: RequestField) => {
        if (sortRef.current === undefined || sortRef.current.field !== sortField) {
            setSorting({field: sortField, operator: '>'});
        } else if (sortRef.current.field === sortField && sortRef.current.operator === '>') {
            setSorting({field: sortField, operator: '<'});
        } else {
            setSorting(undefined);
        }
    };

    const requestsFilter = () => {
        if (requestType === 'pending') {
            return requests.filter(r => !r.is_approved && r.approved_by === null)
        } else if (requestType === 'approved') {
            return requests.filter(r => r.is_approved && r.approved_by !== null)
        } else if (requestType === 'rejected') {
            return requests.filter(r => !r.is_approved && r.approved_by !== null)
        }
        return requests
    }

    const requestsForActivePane = requestsFilter()
    const isShowActionsCell = tableActions.length > 0 && requestsForActivePane.length > 0 && requestType !== "approved" && requestType !== "rejected"

    return (
        <div>
            {payloadView &&
                <FileWindow
                    file={payloadView}
                    onClose={() => setPayloadView(undefined)}
                />
            }


            {currentAlert.length > 0 &&
                <MessageWindow
                    size="small"
                    message={currentAlert}
                    onClose={() => setAlert('')}
                />
            }
            <SyncButton load={load} color={envColor}/>

            {loading ?
                <Dimmer inverted active={true}><Loader/></Dimmer> :
                <Table definition={isShowActionsCell} rows={requestsForActivePane} pageSize={10} sort={sort}>
                    {isShowActionsCell && <ActionsCell header='' actions={tableActions}/>}
                    <TextCell header='Submitted by' value={r => r.submitted_by}/>
                    {requestType !== 'pending' &&
                        <TextCell header={requestType.charAt(0).toUpperCase() + requestType.slice(1) + ' by'}
                                  value={r => r.approved_by}/>}
                    <TextCell header='Url'
                              headerOnClick={() => sortByColumn('url')}
                              value={r => r.url}/>
                    <ButtonCell header='Payload' value={r => [r.body, r.url]} icon='file' onClick={openPayload} color={envColor}/>
                    <TextCell header='Created at'
                              headerOnClick={() => sortByColumn('created_at')}
                              value={r => r.created_at}/>
                </Table>
            }
        </div>
    );
};


const TabPanes: FC<{}> = () => {
    const panes = [
        {menuItem: 'Pending', render: () => <Tab.Pane><Requests requestType={'pending'}/></Tab.Pane>},
        {menuItem: 'Approved', render: () => <Tab.Pane><Requests requestType={'approved'}/></Tab.Pane>},
        {menuItem: 'Rejected', render: () => <Tab.Pane><Requests requestType={'rejected'}/></Tab.Pane>},
    ]

    return (
        <Container style={{margin: '20px 0 0 0'}}>
            <Tab panes={panes}/>
        </Container>
    )
}

export default TabPanes;
